import { SecretValue } from "client/Api/Apps/common";
import { environmentHttpClient } from "client/Api/httpClient";

export type LinkConfiguration = {
	exchange: boolean;
	fileServers: boolean;
	environmentApps: readonly number[];
	integrationFlowAuthentications: readonly number[];
};

export type CreateIntegrationCredentialsSaveModel = {
	username: string;
	password: string;
	confirmPassword: string;
	links: LinkConfiguration;
};

export type UpdateIntegrationCredentialsSaveModel = {
	username: string;
	password: SecretValue;
	confirmPassword: SecretValue;
	links: LinkConfiguration;
};

export type CreateOrEditIntegrationCredentialsViewModel = {
	isEditingExistingCredentials: boolean;
	availableLinks: LinksViewModel;
	model: CreateOrEditIntegrationCredentialsModel;
	alreadyConfiguredLinks: AlreadyConfiguredLinks;
};

export type CreateOrEditIntegrationCredentialsModel = {
	id: number | null;
	username: string;
	passwordContainsValue: boolean;
	links: LinkConfiguration;
};

export type AlreadyConfiguredLinks = {
	exchangeLogin: string | null;
	fileServersLogin: string | null;
	environmentApps: readonly AlreadyConfiguredEnvironmentAppLinkWithCredentialName[];
	integrationFlowAuthentications: readonly AlreadyConfiguredIntegrationFlowAuthenticationWithCredentialName[];
};

export type AlreadyConfiguredEnvironmentAppLinkWithCredentialName = {
	id: number;
	credentialUsername: string;
};

export type AlreadyConfiguredIntegrationFlowAuthenticationWithCredentialName = {
	id: number;
	credentialUsername: string;
};

export type IntegrationCredentialsOverviewViewModel = {
	credentials: IntegrationCredentialsOverviewModel[];
};

export type IntegrationCredentialsOverviewModel = {
	id: number;
	username: string;
	links: LinksViewModel;
};

export type LinksViewModel = {
	fileServers: FileServerLinkViewModel;
	exchange: ExchangeLinkViewModel;
	environmentApps: readonly EnvironmentAppListItem[];
	integrationFlowAuthentications : readonly IntegrationFlowAuthenticationLinkItemModel[];
};

export type ExchangeLinkViewModel = {
	isAvailable: boolean;
	doesSupportNonUpnUsername: boolean;
};

export type FileServerLinkViewModel = {
	isAvailable: boolean;
	doesSupportNonUpnUsername: boolean;
};

export type EnvironmentAppListItem = {
	id: number;
	name: string;
	doesSupportNonUpnUsername: boolean;
};

export type IntegrationFlowAuthenticationLinkItemModel = {
	id: number;
	name: string;
	doesSupportNonUpnUsername: boolean;
};

export function list(
	signal?: AbortSignal
): Promise<IntegrationCredentialsOverviewViewModel> {
	return environmentHttpClient.get<IntegrationCredentialsOverviewViewModel>(
		"UiApi/IntegrationCredentials",
		undefined,
		{ signal }
	);
}

export function getViewModelForNewCredential(signal?: AbortSignal): Promise<CreateOrEditIntegrationCredentialsViewModel> {
	return environmentHttpClient.get<CreateOrEditIntegrationCredentialsViewModel>(
		"UiApi/IntegrationCredentials/New",
		undefined,
		{ signal }
	);
}

export function getViewModelForEditCredential(
	credentialsId: number,
	signal?: AbortSignal
): Promise<CreateOrEditIntegrationCredentialsViewModel> {
	return environmentHttpClient.get<CreateOrEditIntegrationCredentialsViewModel>(
		`UiApi/IntegrationCredentials/Edit/${credentialsId}`,
		undefined,
		{ signal }
	);
}

export function create(
	model: CreateIntegrationCredentialsSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		"UiApi/IntegrationCredentials",
		model,
		{ signal }
	);
}

export function update(
	credentialsId: number,
	model: UpdateIntegrationCredentialsSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/IntegrationCredentials/${credentialsId}`,
		model,
		{ signal }
	);
}

export function addLinksToCredentials(
	credentialsId: number,
	links: LinkConfiguration,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		`UiApi/IntegrationCredentials/${credentialsId}/Links`,
		links,
		{ signal }
	);
}

export function remove(
	credentialsIds: readonly number[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.delete<void>(
		"UiApi/IntegrationCredentials",
		credentialsIds,
		{ signal }
	);
}

export type IntegrationCredentialsListItem = {
	id: number;
	username: string;
};

export type LinkToIntegrationCredentialsDialogViewModel = {
	linkName: string;
	credentials: IntegrationCredentialsListItem[];
};

export function getLinkToIntegrationCredentialsDialogViewModel(
	links: LinkConfiguration,
	signal?: AbortSignal
): Promise<LinkToIntegrationCredentialsDialogViewModel> {
	return environmentHttpClient.post<LinkToIntegrationCredentialsDialogViewModel>(
		`UiApi/IntegrationCredentials/LinkToIntegrationCredentialsViewModel`,
		links,
		{ signal }
	);
}

export type UpdateIntegrationCredentialsPasswordViewModel = {
	id: number;
	username: string;
	linkNames: string[];
};

export type UpdateIntegrationCredentialsPasswordSaveModel = {
	password: string;
	confirmPassword: string;
};

export function updatePassword(
	credentialsId: number,
	model: UpdateIntegrationCredentialsPasswordSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/IntegrationCredentials/${credentialsId}/Password`,
		model,
		{ signal }
	);
}

export function ignorePossiblePasswordChange(
	credentialsId: number
): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/IntegrationCredentials/${credentialsId}/IgnorePossiblePasswordChange`
	);
}

export const enum IntegrationCredentialsLinkKind {
	FileServers = 1,
	Exchange = 2,
	EnvironmentApp = 3,
	IntegrationFlowAuthentication = 4
}

export type LinkIdentifierModel = {
	kind: IntegrationCredentialsLinkKind.FileServers;
}
	|
{
	kind: IntegrationCredentialsLinkKind.Exchange;
}
	|
{
	kind: IntegrationCredentialsLinkKind.EnvironmentApp;
	appId: number;
}
	|
{
	kind: IntegrationCredentialsLinkKind.IntegrationFlowAuthentication;
	authenticationId: number;
};

export type PasswordStateResultModel = {
	isPossibleChanged: false;
}
	|
{
	isPossibleChanged: true;
	changedViewModel: UpdateIntegrationCredentialsPasswordViewModel;
};

export function checkLinkPasswordState(
	linkIdentifier: LinkIdentifierModel
): Promise<PasswordStateResultModel> {
	return environmentHttpClient.get<PasswordStateResultModel>(
		"UiApi/IntegrationCredentials/CheckLinkPasswordState",
		linkIdentifier
	);
}
